import { Component, OnInit } from "@angular/core";
import { MatDialog } from "@angular/material/dialog";
import { MatSnackBar } from "@angular/material/snack-bar";
import { CdkDragDrop, moveItemInArray } from "@angular/cdk/drag-drop";
import { AdminAdmDefinitionService } from "../../../../api/services/admin-adm-definition.service";
import { AdmDefinitionResponse } from "../../../../api/models/adm-definition-response";
import { AdmTable } from "../../../../api/models/adm-table";
import { AdmTableColumn } from "../../../../api/models/adm-table-column";
import { AdmFieldGroup } from "../../../../api/models/adm-field-group";
import { AdmRelationship } from "../../../../api/models/adm-relationship";
import { AdmFilterPointEx } from "../../../../api/models/adm-filter-point-ex";
import { AdmDefinitionIssue } from "../../../../api/models/adm-definition-issue";
import { AdmSaveFlattenedColumnRequest } from "../../../../api/models/adm-save-flattened-column-request";
import { AdmSaveResult } from "../../../../api/models/adm-save-result";
import { DialogResultType } from "../../../../dialogs/DialogResult";
import { AdmTableDialogComponent } from "../../../../dialogs/adm-table-dialog/adm-table-dialog.component";
import { AdmFieldGroupDialogComponent } from "../../../../dialogs/adm-field-group-dialog/adm-field-group-dialog.component";
import { AdmColumnDialogComponent } from "../../../../dialogs/adm-column-dialog/adm-column-dialog.component";
import { AdmRelationshipDialogComponent } from "../../../../dialogs/adm-relationship-dialog/adm-relationship-dialog.component";
import { AdmFlattenedColumnDialogComponent } from "../../../../dialogs/adm-flattened-column-dialog/adm-flattened-column-dialog.component";
import { AdmRerouteLineageDialogComponent } from "../../../../dialogs/adm-reroute-lineage-dialog/adm-reroute-lineage-dialog.component";
import { AdmFilterPointDialogComponent } from "../../../../dialogs/adm-filter-point-dialog/adm-filter-point-dialog.component";

// The numeric unions mirror the C# enum member order (PublicInsight.Db/Models/Adm*.cs).
export const SIZE_HINTS = [{ value: 0, label: "Unknown" }, { value: 1, label: "Small (broadcast / copy whole)" }, { value: 2, label: "Large" }];
export const CARDINALITIES = [{ value: 0, label: "One to one" }, { value: 1, label: "Many to one (lookup)" }, { value: 2, label: "One to many" }];
export const RELATIONSHIP_KINDS = [{ value: 0, label: "Reference (walk only)" }, { value: 1, label: "Flatten into parent" }];
// Only meaningful on a one-to-many relationship: what a filter on the many (To) side's own column does to
// the many side's own materialized rows when the ADM is built.
export const FILTER_SCOPES = [
  { value: 0, label: "Restrict (keep only the matching rows)" },
  { value: 1, label: "Qualify (keep every row of a matching group)" },
];
export const FILTER_TYPES = [{ value: 0, label: "In list" }, { value: 1, label: "Date range" }, { value: 2, label: "Exists via bridge table" }, { value: 3, label: "Comparison" }];
// Standard SQL comparison operators only - mirrors AdmDefinitionValidator.ComparisonOperators, which rejects
// anything outside this set (the notebook interpolates the operator directly into SQL). Equality against a
// boolean/bit column (e.g. "= 1") is just a comparison with "="; there is no separate boolean filter type.
export const COMPARISON_OPERATORS = [
  { value: ">", label: "Greater than (>)" },
  { value: ">=", label: "Greater than or equal (>=)" },
  { value: "<", label: "Less than (<)" },
  { value: "<=", label: "Less than or equal (<=)" },
  { value: "=", label: "Equal (=)" },
  { value: "<>", label: "Not equal (<>)" },
];

// Admin authoring of the Agent Data Model Tables Definition: tables, columns, relationships, flattened columns
// (including chains) and filter points. Every save returns the definition's validation issues; nothing invalid can
// reach an ADM because the server refuses to materialize from a definition with errors.
@Component({
  selector: "admin-adm-definition",
  templateUrl: "./admin-adm-definition.component.html",
})
export class AdminAdmDefinitionComponent implements OnInit {
  public loading = true;
  public definition: AdmDefinitionResponse = { sources: [], tables: [], columns: [], fieldGroups: [], relationships: [], filterPoints: [], issues: [] };
  public issues: AdmDefinitionIssue[] = [];

  public sizeHints = SIZE_HINTS;
  public cardinalities = CARDINALITIES;
  public relationshipKinds = RELATIONSHIP_KINDS;
  public filterTypes = FILTER_TYPES;

  public selectedTableId: number | null = null;

  // ---- bulk move of columns between field groups ----
  public selectedColumnIds = new Set<number>();
  // "" = none chosen yet, "none" = ungrouped, "new" = a new field group, "g:<id>" = an existing field group.
  public moveTargetValue = "";
  public newGroupName = "";
  public moving = false;

  // ---- bulk setting of column flags ----
  // Constant, so the dropdowns' options never change identity.
  public readonly flagChoices = [{ value: "", label: "No change" }, { value: "on", label: "On" }, { value: "off", label: "Off" }];
  public flagValues = { selectable: "", alwaysKeep: "", isSensitive: "", isHighCardinality: "" };
  public applyingFlags = false;
  // Built once per load / table change, never in a getter: dropdown options must keep the same identity between
  // change-detection passes or the select keeps re-creating them.
  public moveTargets: { value: string; label: string }[] = [];
  // Tab order: Tables & Columns, Relationships, Flattened Columns, Filter Points, Reuse Flattened Columns, Import / Export, Validation.
  public selectedTab = 0;
  public readonly validationTabIndex = 6;

  constructor(private definitionService: AdminAdmDefinitionService, private snackBar: MatSnackBar, private dialog: MatDialog) {}

  ngOnInit() {
    this.load();
  }

  load() {
    this.loading = true;
    this.definitionService.adminAdmDefinitionGetDefinition().subscribe({
      next: (definition) => {
        this.definition = definition;
        this.issues = definition.issues || [];
        this.pruneSelection();
        if (this.selectedTableId === null && (definition.tables || []).length > 0) this.selectedTableId = definition.tables[0].admTableId;
        this.refreshMoveTargets();
        this.refreshStaleCount();
        this.loading = false;
      },
      error: () => {
        this.loading = false;
        this.snackBar.open("Could not load the Tables Definition", null, { duration: 5000 });
      },
    });
  }

  // ---- lookups ----------------------------------------------------------------------------------------
  get tables(): AdmTable[] { return this.definition.tables || []; }
  get columns(): AdmTableColumn[] { return this.definition.columns || []; }
  get fieldGroups(): AdmFieldGroup[] { return this.definition.fieldGroups || []; }
  get relationships(): AdmRelationship[] { return this.definition.relationships || []; }
  get sources() { return this.definition.sources || []; }
  get errorCount(): number { return this.issues.filter((i) => i.severity === 1).length; }
  get warningCount(): number { return this.issues.filter((i) => i.severity === 0).length; }

  tableName(id: number): string {
    const t = this.tables.find((x) => x.admTableId === id);
    return t ? t.tableName : `#${id}`;
  }

  columnLabel(id: number): string {
    const c = this.columns.find((x) => x.admTableColumnId === id);
    return c ? `${this.tableName(c.admTableId)}.${c.outputName || c.columnName}` : `#${id}`;
  }

  columnsOf(tableId: number | null): AdmTableColumn[] {
    return this.columns.filter((c) => c.admTableId === tableId);
  }

  fieldGroupsOf(tableId: number | null): AdmFieldGroup[] {
    return this.fieldGroups.filter((g) => g.admTableId === tableId).sort((a, b) => a.sortOrder - b.sortOrder || a.name.localeCompare(b.name));
  }

  fieldGroupName(id: number | null | undefined): string {
    if (id == null) return "(ungrouped)";
    const g = this.fieldGroups.find((x) => x.admFieldGroupId === id);
    return g ? g.name : `#${id}`;
  }

  // Columns of the currently selected table, bucketed under their field group in the table's field-group order, with
  // ungrouped columns in a trailing bucket - mirrors how the picker on the layout dialog groups the same data.
  //
  // Called directly from *ngFor in the template (there is no cached/memoized version), so it builds brand new group
  // and column-list wrapper objects on every single change-detection run - mousing over the page, a keystroke
  // anywhere, anything zone.js schedules a check for. Without trackBy, *ngFor's default identity diffing sees a
  // "new" group object each time and tears down and rebuilds that group's entire DOM subtree, including every
  // column row's edit link, on every tick. A click's mousedown can land on a link that CD has already replaced by
  // the time the click/mouseup fires, so the edit link "does nothing" - not a broken handler, a handler that is no
  // longer attached to anything by the time the click completes. trackBy below keeps the DOM node identity stable
  // by the field group's own id (which does NOT change every tick) so a link a user pressed down on is still the
  // link that receives the click.
  columnsByFieldGroup(tableId: number | null): { admFieldGroupId: number | null; name: string; columns: AdmTableColumn[] }[] {
    const cols = this.columnsOf(tableId);
    const groups = this.fieldGroupsOf(tableId).map((g) => ({
      admFieldGroupId: g.admFieldGroupId, name: g.name, columns: cols.filter((c) => c.admFieldGroupId === g.admFieldGroupId),
    }));
    const ungrouped = cols.filter((c) => c.admFieldGroupId == null);
    if (ungrouped.length > 0 || groups.length === 0) groups.push({ admFieldGroupId: null, name: "(ungrouped)", columns: ungrouped });
    return groups;
  }

  trackByFieldGroupId(_index: number, group: { admFieldGroupId: number | null }): number | null {
    return group.admFieldGroupId;
  }

  trackByColumnId(_index: number, c: AdmTableColumn): number {
    return c.admTableColumnId;
  }

  // Drag-to-reorder within one field-group bucket (physical and flattened columns together, same bucket
  // columnsByFieldGroup already shows). `group` is a throwaway wrapper columnsByFieldGroup rebuilds every CD
  // tick, so there is no point keeping the reordered array around locally - persist it, then reload() (same
  // as every other save in this component) to pick the new, authoritative order back up from the server.
  dropColumn(group: { columns: AdmTableColumn[] }, event: CdkDragDrop<AdmTableColumn[]>) {
    if (event.previousIndex === event.currentIndex) return;
    moveItemInArray(group.columns, event.previousIndex, event.currentIndex);
    this.definitionService.adminAdmDefinitionReorderColumns({ orderedColumnIds: group.columns.map((c) => c.admTableColumnId) }).subscribe({
      next: (r) => this.done(r, "Column order saved"),
      error: (e) => this.failed(e),
    });
  }

  // ---- bulk move: selection ----
  onTableChange() {
    this.selectedColumnIds.clear();
    this.moveTargetValue = "";
    this.newGroupName = "";
    this.resetFlags();
    this.refreshMoveTargets();
  }

  // Keeps only selected columns that still exist on the selected table (after a reload, a table change, ...).
  private pruneSelection() {
    const valid = new Set(this.columnsOf(this.selectedTableId).map((c) => c.admTableColumnId));
    Array.from(this.selectedColumnIds).filter((id) => !valid.has(id)).forEach((id) => this.selectedColumnIds.delete(id));
  }

  private refreshMoveTargets() {
    this.moveTargets = [
      ...this.fieldGroupsOf(this.selectedTableId).map((g) => ({ value: `g:${g.admFieldGroupId}`, label: g.name })),
      { value: "none", label: "(ungrouped)" },
      { value: "new", label: "New field group..." },
    ];
    // A target that no longer exists (a group deleted in between) is dropped.
    if (this.moveTargetValue !== "" && !this.moveTargets.some((t) => t.value === this.moveTargetValue)) this.moveTargetValue = "";
  }

  toggleColumn(id: number, on: boolean) {
    on ? this.selectedColumnIds.add(id) : this.selectedColumnIds.delete(id);
  }

  groupAllSelected(group: { columns: AdmTableColumn[] }): boolean {
    return group.columns.length > 0 && group.columns.every((c) => this.selectedColumnIds.has(c.admTableColumnId));
  }

  groupSomeSelected(group: { columns: AdmTableColumn[] }): boolean {
    return !this.groupAllSelected(group) && group.columns.some((c) => this.selectedColumnIds.has(c.admTableColumnId));
  }

  toggleGroup(group: { columns: AdmTableColumn[] }, on: boolean) {
    group.columns.forEach((c) => this.toggleColumn(c.admTableColumnId, on));
  }

  clearSelection() {
    this.selectedColumnIds.clear();
  }

  get canMove(): boolean {
    if (this.selectedColumnIds.size === 0 || this.moving || this.moveTargetValue === "") return false;
    return this.moveTargetValue !== "new" || this.newGroupName.trim().length > 0;
  }

  // ---- bulk setting of column flags ----
  private resetFlags() {
    this.flagValues = { selectable: "", alwaysKeep: "", isSensitive: "", isHighCardinality: "" };
  }

  get canApplyFlags(): boolean {
    const v = this.flagValues;
    return this.selectedColumnIds.size > 0 && !this.applyingFlags && (v.selectable !== "" || v.alwaysKeep !== "" || v.isSensitive !== "" || v.isHighCardinality !== "");
  }

  private flagRequest(dryRun: boolean) {
    const tri = (value: string): boolean | undefined => (value === "" ? undefined : value === "on");
    const v = this.flagValues;
    return {
      columnIds: Array.from(this.selectedColumnIds),
      selectable: tri(v.selectable), alwaysKeep: tri(v.alwaysKeep), isSensitive: tri(v.isSensitive), isHighCardinality: tri(v.isHighCardinality),
      dryRun,
    };
  }

  // Previews what would change, tells the admin what it affects, and only then applies it.
  applyFlags() {
    if (!this.canApplyFlags) return;
    this.applyingFlags = true;
    this.definitionService.adminAdmDefinitionSetColumnFlags(this.flagRequest(true)).subscribe({
      next: (preview) => {
        const o = preview.outcome;
        if (o.changed === 0) {
          this.applyingFlags = false;
          this.snackBar.open(o.skipped && o.skipped.length > 0 ? `Nothing to change. ${o.skipped.join("; ")}` : "Nothing to change: the selected columns already have those settings", null, { duration: 6000 });
          return;
        }
        const chosen = [
          ["Selectable", this.flagValues.selectable], ["Key", this.flagValues.alwaysKeep],
          ["Sensitive", this.flagValues.isSensitive], ["High cardinality", this.flagValues.isHighCardinality],
        ].filter((x) => x[1] !== "").map((x) => `${x[0]} = ${x[1]}`).join(", ");
        const count = this.selectedColumnIds.size;
        const lines = [`Apply to the ${count} selected column${count === 1 ? "" : "s"}: ${chosen}?`, "", `${o.changed} will change, ${o.unchanged} already match.`];
        if (o.skipped && o.skipped.length > 0) lines.push(`Not applied: ${o.skipped.join("; ")}.`);
        if (o.layoutsAffected > 0)
          lines.push("", `Warning: ${o.columnsInUse} of these column(s) are chosen in ${o.layoutsAffected} ADM(s). Those ADMs will fail to refresh until the columns are selectable again or removed from them.`);
        if (!confirm(lines.join("\n"))) { this.applyingFlags = false; return; }

        this.definitionService.adminAdmDefinitionSetColumnFlags(this.flagRequest(false)).subscribe({
          next: (r) => {
            this.applyingFlags = false;
            this.resetFlags();
            this.done(r, `Updated ${r.outcome.changed} column${r.outcome.changed === 1 ? "" : "s"}`);
          },
          error: (e) => { this.applyingFlags = false; this.failed(e); },
        });
      },
      error: (e) => { this.applyingFlags = false; this.failed(e); },
    });
  }

  moveSelected() {
    if (!this.canMove) return;
    const request: { columnIds: number[]; fieldGroupId?: number; newFieldGroupName?: string } = { columnIds: Array.from(this.selectedColumnIds) };
    if (this.moveTargetValue === "new") request.newFieldGroupName = this.newGroupName.trim();
    else if (this.moveTargetValue.startsWith("g:")) request.fieldGroupId = Number(this.moveTargetValue.substring(2));
    const count = request.columnIds.length;
    this.moving = true;
    this.definitionService.adminAdmDefinitionMoveColumns(request).subscribe({
      next: (r) => {
        this.moving = false;
        this.selectedColumnIds.clear();
        this.moveTargetValue = "";
        this.newGroupName = "";
        this.done(r, `Moved ${count} column${count === 1 ? "" : "s"}`);
      },
      error: (e) => {
        this.moving = false;
        this.failed(e);
      },
    });
  }

  physicalColumns(): AdmTableColumn[] {
    return this.columns.filter((c) => c.columnKind === 0);
  }

  relationshipLabel(r: AdmRelationship): string {
    return `${this.columnLabel(r.fromColumnId)} → ${this.columnLabel(r.toColumnId)}`;
  }

  private done(result: AdmSaveResult, message: string) {
    this.issues = result.issues || [];
    this.snackBar.open(message, null, { duration: 3000 });
    this.load();
  }

  private failed(error: any) {
    const message = (error && (error.error || error.message)) || "Save failed";
    this.snackBar.open(typeof message === "string" ? message : "Save failed", null, { duration: 8000 });
  }

  // ---- tables -----------------------------------------------------------------------------------------
  newTable() {
    const source = this.sources[0];
    this.openTableDialog({ admTableId: 0, admSourceId: source ? source.admSourceId : 0, tableName: "", displayName: "", description: "", copyWhole: false, sizeHint: 0, retired: false });
  }

  editTable(table: AdmTable) {
    this.openTableDialog({ ...table });
  }

  editSelectedTable() {
    const table = this.tables.find((t) => t.admTableId === this.selectedTableId);
    if (table) this.editTable(table);
  }

  private openTableDialog(table: AdmTable) {
    AdmTableDialogComponent.showDialog(this.dialog, { table, sources: this.sources }).subscribe((result) => {
      if (!result || result.ResultType === DialogResultType.Cancel) return;
      if (result.ResultType === DialogResultType.Delete) {
        this.setRetired("Table", result.ResultData.admTableId, !result.ResultData.retired);
        return;
      }
      this.definitionService.adminAdmDefinitionSaveTable(result.ResultData).subscribe({
        next: (r) => this.done(r, "Table saved"),
        error: (e) => this.failed(e),
      });
    });
  }

  // ---- field groups (display-only grouping of a table's columns) --------------------------------------
  newFieldGroup() {
    if (this.selectedTableId === null) return;
    this.openFieldGroupDialog({ admFieldGroupId: 0, admTableId: this.selectedTableId, name: "", sortOrder: this.fieldGroupsOf(this.selectedTableId).length });
  }

  editFieldGroup(group: AdmFieldGroup) {
    this.openFieldGroupDialog({ ...group });
  }

  private openFieldGroupDialog(fieldGroup: AdmFieldGroup) {
    AdmFieldGroupDialogComponent.showDialog(this.dialog, { fieldGroup }).subscribe((result) => {
      if (!result || result.ResultType === DialogResultType.Cancel) return;
      if (result.ResultType === DialogResultType.Delete) {
        this.deleteFieldGroup(result.ResultData);
        return;
      }
      this.definitionService.adminAdmDefinitionSaveFieldGroup(result.ResultData).subscribe({
        next: (r) => this.done(r, "Field group saved"),
        error: (e) => this.failed(e),
      });
    });
  }

  deleteFieldGroup(group: AdmFieldGroup) {
    if (!confirm(`Delete the field group "${group.name}"? Its columns become ungrouped.`)) return;
    this.definitionService.adminAdmDefinitionDeleteFieldGroup(group.admFieldGroupId).subscribe({
      next: (r) => this.done(r, "Field group deleted"),
      error: (e) => this.failed(e),
    });
  }

  // ---- columns (physical) -----------------------------------------------------------------------------
  newColumn() {
    if (this.selectedTableId === null) return;
    this.openColumnDialog({
      admTableColumnId: 0, admTableId: this.selectedTableId, columnName: "", outputName: "", displayName: "", description: "",
      sourceType: "", warehouseType: "", selectable: true, alwaysKeep: false, isSensitive: false, isHighCardinality: false, retired: false, columnKind: 0,
      admFieldGroupId: null, sortOrder: this.columnsOf(this.selectedTableId).filter((c) => c.admFieldGroupId == null).length,
    });
  }

  editColumn(column: AdmTableColumn) {
    this.openColumnDialog({ ...column });
  }

  private openColumnDialog(column: AdmTableColumn) {
    AdmColumnDialogComponent.showDialog(this.dialog, { column, fieldGroups: this.fieldGroupsOf(column.admTableId) }).subscribe((result) => {
      if (!result || result.ResultType === DialogResultType.Cancel) return;
      if (result.ResultType === DialogResultType.Delete) {
        this.setRetired("Column", result.ResultData.admTableColumnId, !result.ResultData.retired);
        return;
      }
      // a blank warehouse type means "the warehouse cannot store this" - the server then forces it non-selectable
      const form = { ...result.ResultData, warehouseType: result.ResultData.warehouseType ? result.ResultData.warehouseType : null };
      this.definitionService.adminAdmDefinitionSaveColumn(form).subscribe({
        next: (r) => this.done(r, "Column saved"),
        error: (e) => this.failed(e),
      });
    });
  }

  // ---- relationships ----------------------------------------------------------------------------------
  // Relationships whose parent (From side) is the given table - what the Relationships tab's table filter
  // shows, since a relationship is really only ever thought of from its parent's side.
  relationshipsOf(tableId: number | null): AdmRelationship[] {
    return this.relationships.filter((r) => {
      const from = this.columns.find((c) => c.admTableColumnId === r.fromColumnId);
      return from ? from.admTableId === tableId : false;
    });
  }

  newRelationship() {
    // Default From to the selected table's own first physical column, so a relationship started from the
    // Relationships tab's table filter already points the right way; the admin can still change it.
    const defaultFromColumnId = this.physicalColumns().find((c) => c.admTableId === this.selectedTableId)?.admTableColumnId || 0;
    this.openRelationshipDialog({ admRelationshipId: 0, fromColumnId: defaultFromColumnId, toColumnId: 0, cardinality: 1, kind: 0, joinType: 0, flattenPrefix: "", absorbTarget: true, filterScope: 0, retired: false });
  }

  editRelationship(r: AdmRelationship) {
    this.openRelationshipDialog({ ...r });
  }

  // Flattened columns whose path walks through this relationship - their value is meaningless without that hop,
  // so deleting the relationship takes them with it (see DeleteRelationship on the server).
  flattenedColumnsUsing(relationshipId: number): AdmTableColumn[] {
    return this.flattenedColumns().filter((c) =>
      (c.flattenRelationshipPath || "").split(",").filter((x) => x).map((x) => Number(x)).includes(relationshipId)
    );
  }

  private openRelationshipDialog(relationship: AdmRelationship) {
    AdmRelationshipDialogComponent.showDialog(this.dialog, {
      relationship, tables: this.tables, physicalColumns: this.physicalColumns(), columnLabel: (id) => this.columnLabel(id),
      dependentFlattenedColumns: this.flattenedColumnsUsing(relationship.admRelationshipId),
    }).subscribe((result) => {
      if (!result || result.ResultType === DialogResultType.Cancel) return;
      if (result.ResultType === DialogResultType.Delete) {
        this.setRetired("Relationship", result.ResultData.admRelationshipId, !result.ResultData.retired);
        return;
      }
      if (result.ResultType === DialogResultType.Yes) {
        this.definitionService.adminAdmDefinitionDeleteRelationship(result.ResultData.admRelationshipId).subscribe({
          next: (r) => this.done(r, "Relationship deleted"),
          error: (e) => this.failed(e),
        });
        return;
      }
      const f = result.ResultData;
      this.definitionService.adminAdmDefinitionSaveRelationship({
        admRelationshipId: f.admRelationshipId, fromColumnId: f.fromColumnId, toColumnId: f.toColumnId, cardinality: f.cardinality,
        kind: f.kind, sourceTableOverride: f.sourceTableOverride, flattenPrefix: f.flattenPrefix, absorbTarget: f.absorbTarget,
        filterScope: f.filterScope,
      }).subscribe({
        next: (r) => this.done(r, "Relationship saved"),
        error: (e) => this.failed(e),
      });
    });
  }

  // ---- flattened columns (chains) ---------------------------------------------------------------------
  flattenedColumns(): AdmTableColumn[] {
    return this.columns.filter((c) => c.columnKind === 1);
  }

  // A flattened column's own admTableId IS its parent table (it's a virtual column of the parent) - no
  // relationship walk needed, unlike relationshipsOf.
  flattenedColumnsOf(tableId: number | null): AdmTableColumn[] {
    return this.flattenedColumns().filter((c) => c.admTableId === tableId);
  }

  public flattenedSearch = "";

  // The selected table's flattened columns narrowed by the search box: matches the output name, field group,
  // relationship path or source column (case-insensitive), so a column can be found by where it comes from too.
  searchedFlattenedColumns(tableId: number | null): AdmTableColumn[] {
    const term = (this.flattenedSearch || "").trim().toLowerCase();
    const all = this.flattenedColumnsOf(tableId);
    if (!term) return all;
    return all.filter((c) =>
      [
        c.outputName, this.fieldGroupName(c.admFieldGroupId), this.pathLabel(c.flattenRelationshipPath),
        c.flattenSourceColumnId ? this.columnLabel(c.flattenSourceColumnId) : "",
      ].some((text) => (text || "").toLowerCase().includes(term))
    );
  }

  pathLabel(path: string | undefined): string {
    if (!path) return "";
    return path.split(",").map((id) => {
      const rel = this.relationships.find((r) => r.admRelationshipId === Number(id));
      return rel ? this.relationshipLabel(rel) : `#${id}`;
    }).join("  →  ");
  }

  newFlattened() {
    // admFieldGroupId left null and ungrouped false: the server defaults the group from the source table/path
    // (see AdmFieldGroupNaming) unless "(ungrouped)" is checked below.
    this.openFlattenedDialog(
      {
        admTableColumnId: 0, parentTableId: this.selectedTableId || 0, outputName: "", displayName: "", description: "",
        relationshipPath: [], sourceColumnId: 0, selectable: true, admFieldGroupId: null, ungrouped: false,
      },
      [],
      false
    );
  }

  editFlattened(column: AdmTableColumn) {
    this.openFlattenedDialog(
      {
        admTableColumnId: column.admTableColumnId, parentTableId: column.admTableId, outputName: column.outputName, displayName: column.displayName,
        description: column.description, relationshipPath: [], sourceColumnId: column.flattenSourceColumnId || 0, selectable: column.selectable,
        admFieldGroupId: column.admFieldGroupId, ungrouped: column.admFieldGroupId == null,
      },
      (column.flattenRelationshipPath || "").split(",").filter((x) => x).map((x) => Number(x)),
      column.retired
    );
  }

  // ADM layouts whose frozen snapshot is behind the current lineage of the selected table's flattened columns.
  public staleLayoutCount = 0;
  public regeneratingStale = false;

  refreshStaleCount() {
    if (!this.selectedTableId) { this.staleLayoutCount = 0; return; }
    const tableId = this.selectedTableId;
    this.definitionService.adminAdmDefinitionGetStaleLayouts(tableId).subscribe({
      next: (r) => { if (tableId === this.selectedTableId) this.staleLayoutCount = r.staleCount; },
      error: () => { this.staleLayoutCount = 0; },
    });
  }

  onFlattenedTableChange() {
    this.staleLayoutCount = 0;
    this.refreshStaleCount();
  }

  regenerateStale() {
    if (!this.selectedTableId || this.staleLayoutCount === 0 || this.regeneratingStale) return;
    if (!confirm(`Regenerate ${this.staleLayoutCount} ADM layout(s) that are out of date with the lineage of ${this.tableName(this.selectedTableId)}'s flattened columns?`)) return;
    this.regeneratingStale = true;
    this.definitionService.adminAdmDefinitionRegenerateStale(this.selectedTableId).subscribe({
      next: (r) => {
        this.regeneratingStale = false;
        const failures = (r.failures || []).length;
        this.snackBar.open(`Regenerating ${r.regeneratedCount} ADM layout(s)` + (failures ? `; ${failures} could not be started: ${r.failures.join(" ")}` : ""), null, { duration: failures ? 10000 : 6000 });
        this.refreshStaleCount();
      },
      error: (e) => { this.regeneratingStale = false; this.failed(e); },
    });
  }

  public regeneratingAll = false;

  // Rebuilds every ADM that has been generated. They share the one generation queue, so this just lines them all up.
  regenerateAll() {
    if (this.regeneratingAll) return;
    this.definitionService.adminAdmDefinitionGetRegenerableLayouts().subscribe({
      next: (count) => {
        if (count.staleCount === 0) { this.snackBar.open("No ADMs to regenerate (none generated yet, or all are already building)", null, { duration: 5000 }); return; }
        if (!confirm(`Regenerate all ${count.staleCount} ADM(s)? They are queued and built a few at a time, so this can take a while.`)) return;
        this.regeneratingAll = true;
        this.definitionService.adminAdmDefinitionRegenerateAll().subscribe({
          next: (r) => {
            this.regeneratingAll = false;
            const failures = (r.failures || []).length;
            this.snackBar.open(`Queued ${r.regeneratedCount} ADM(s) for regeneration` + (failures ? `; ${failures} could not be started: ${r.failures.join(" ")}` : ""), null, { duration: failures ? 10000 : 6000 });
            this.refreshStaleCount();
          },
          error: (e) => { this.regeneratingAll = false; this.failed(e); },
        });
      },
      error: (e) => this.failed(e),
    });
  }

  rerouteLineage() {
    if (!this.selectedTableId) return;
    AdmRerouteLineageDialogComponent.showDialog(this.dialog, {
      parentTableId: this.selectedTableId, tables: this.tables, columns: this.columns, relationships: this.relationships,
    }).subscribe((result) => {
      if (!result || result.ResultType !== DialogResultType.Ok) return;
      const r = result.ResultData;
      this.snackBar.open(`Moved ${r.changes?.length || 0} column(s). Regenerate the affected ADMs when you are ready.`, null, { duration: 6000 });
      this.load();
    });
  }

  private openFlattenedDialog(flattened: AdmSaveFlattenedColumnRequest, relationshipPath: number[], retired: boolean) {
    AdmFlattenedColumnDialogComponent.showDialog(this.dialog, {
      flattened, relationshipPath, retired, tables: this.tables, columns: this.columns, fieldGroups: this.fieldGroups, relationships: this.relationships,
    }).subscribe((result) => {
      if (!result || result.ResultType === DialogResultType.Cancel) return;
      if (result.ResultType === DialogResultType.Delete) {
        this.setRetired("Column", result.ResultData.admTableColumnId, !result.ResultData.retired);
        return;
      }
      if (result.ResultType === DialogResultType.Yes) {
        this.definitionService.adminAdmDefinitionDeleteFlattenedColumn(result.ResultData.admTableColumnId).subscribe({
          next: (r) => this.done(r, "Flattened column deleted"),
          error: (e) => this.failed(e),
        });
        return;
      }
      this.definitionService.adminAdmDefinitionSaveFlattenedColumn(result.ResultData).subscribe({
        next: (r) => this.done(r, "Flattened column saved"),
        error: (e) => this.failed(e),
      });
    });
  }

  // ---- filter points ----------------------------------------------------------------------------------
  // A filter point can target columns on more than one table (AdmFilterPointTarget is its own table), so
  // "belongs to" the selected table means "has at least one target column there" - unlike relationships or
  // flattened columns, which each have exactly one parent table.
  filterPointsOf(tableId: number | null): AdmFilterPointEx[] {
    return (this.definition.filterPoints || []).filter((f) =>
      (f.targetColumnIds || []).some((id) => this.columns.find((c) => c.admTableColumnId === id)?.admTableId === tableId)
    );
  }

  newFilter() {
    // Default target to the selected table's own first physical column, same spirit as newRelationship's
    // From default - the admin can still add targets on other tables.
    const defaultTargetColumnId = this.physicalColumns().find((c) => c.admTableId === this.selectedTableId)?.admTableColumnId;
    this.openFilterDialog({
      admFilterPointId: 0, filterKey: "", displayName: "", filterType: 0, definitionJson: "", retired: false,
      targetColumnIds: defaultTargetColumnId ? [defaultTargetColumnId] : [],
    });
  }

  editFilter(f: AdmFilterPointEx) {
    this.openFilterDialog({ ...f, targetColumnIds: [...(f.targetColumnIds || [])] });
  }

  private openFilterDialog(filterPoint: AdmFilterPointEx) {
    AdmFilterPointDialogComponent.showDialog(this.dialog, {
      filterPoint,
      physicalColumns: this.physicalColumns(),
      columnLabel: (id) => this.columnLabel(id),
      filterKeys: this.definition.availableFilterKeys || [],
    }).subscribe((result) => {
      if (!result || result.ResultType === DialogResultType.Cancel) return;
      if (result.ResultType === DialogResultType.Delete) {
        this.setRetired("FilterPoint", result.ResultData.admFilterPointId, !result.ResultData.retired);
        return;
      }
      this.definitionService.adminAdmDefinitionSaveFilterPoint(result.ResultData).subscribe({
        next: (r) => this.done(r, "Filter point saved"),
        error: (e) => this.failed(e),
      });
    });
  }

  // ---- retire / validate ------------------------------------------------------------------------------
  setRetired(kind: "Table" | "Column" | "Relationship" | "FilterPoint", id: number, retired: boolean) {
    this.definitionService.adminAdmDefinitionSetRetired({ kind, id, retired }).subscribe({
      next: (r) => this.done(r, retired ? "Retired" : "Restored"),
      error: (e) => this.failed(e),
    });
  }

  validate() {
    this.definitionService.adminAdmDefinitionValidate().subscribe((issues) => {
      this.issues = issues || [];
      this.snackBar.open(this.errorCount === 0 ? "No errors" : `${this.errorCount} error(s)`, null, { duration: 3000 });
    });
  }
}
